import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { Formik, FormikProps } from "formik"
import { LoanOfficer } from "../../../../types"
import {
  useAppSelector,
  useToast,
  useValidateEmailAndNMLS
} from "../../../hooks"
import SidePanel from "../../side-panel"
import { selectUserDetails } from "../../../../redux/slices/auth"
import {
  useFetchBrandQuery,
  useFetchBrandsQuery,
  useUpdateLoanOfficerMutation
} from "../../../../redux/queries"
import { getLoFormSchema } from "./lo-form-schema"
import ManageLoForm from "./form-content"
import If from "../../if"
import { checkIsSuperAdmin, normalizeUrl } from "../../../utils"
import { defaultValues } from "./defaultValues"

interface Props {
  data?: LoanOfficer
  brandId?: string
  open: boolean
  tabsVisible?: boolean
  onClose: () => void
  setFormRef?: (formRef: RefObject<FormikProps<LoanOfficer>>) => void
  isInSidePanel: boolean
}

const EditLoProfile: FC<Props> = ({
  data,
  brandId,
  open,
  tabsVisible = true,
  onClose,
  setFormRef,
  isInSidePanel
}) => {
  const { pushErrorToast, pushSuccessToast } = useToast()

  const { validateLO } = useValidateEmailAndNMLS()

  const validate = useCallback(
    (lo: LoanOfficer) => validateLO(lo),
    [validateLO]
  )

  const user = useAppSelector(selectUserDetails)
  const validationSchema = useMemo(() => getLoFormSchema(brandId), [brandId])

  const [
    updateLoanOfficer,
    { data: response, isSuccess, isError, isLoading, reset }
  ] = useUpdateLoanOfficerMutation()

  const { data: brandDetails } = useFetchBrandQuery(data?.bank_nid ?? "", {
    skip: !data?.bank_nid
  })

  const isSuperAdmin = checkIsSuperAdmin(user?.roles ?? [])

  const { data: brands } = useFetchBrandsQuery(
    isSuperAdmin
      ? { limit: 999 }
      : { org_id: brandDetails?.org_id || user?.org?.id },
    {
      skip: !isSuperAdmin && !brandDetails?.org_id && !user?.org?.id,
      refetchOnMountOrArgChange: true
    }
  )

  const [loading, setLoading] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const formRef = useRef<FormikProps<LoanOfficer>>(null)

  const isCreate = !(data?.uid || data?.loid)

  const setFormField = useCallback((name: string, val: string | number) => {
    formRef.current?.setFieldValue(name, val)
  }, [])

  const resetForm = useCallback(() => {
    setLoading(false)
    formRef.current?.resetForm()
    reset()
  }, [reset])

  const onSubmit = async () => {
    if (formRef.current?.values) {
      const values = formRef.current.values

      // Normalize all URL fields before submission
      const normalizedValues: LoanOfficer = {
        ...values,
        app_link: normalizeUrl(values.app_link) || values.app_link || "",
        website_link:
          normalizeUrl(values.website_link) || values.website_link || "",
        facebook_link:
          normalizeUrl(values.facebook_link) || values.facebook_link || "",
        linkedin_link:
          normalizeUrl(values.linkedin_link) || values.linkedin_link || "",
        calendar_link:
          normalizeUrl(values.calendar_link) || values.calendar_link || "",
        instagram_link:
          normalizeUrl(values.instagram_link) || values.instagram_link || "",
        youtube_link:
          normalizeUrl(values.youtube_link) || values.youtube_link || "",
        special_disclosure_link:
          normalizeUrl(values.special_disclosure_link) ||
          values.special_disclosure_link ||
          ""
      }

      updateLoanOfficer(normalizedValues)
    }
  }

  const submitFormHandler = () => {
    setIsSubmitting(true)
    formRef.current?.handleSubmit()
  }

  const handleClose = () => {
    onClose()
    formRef.current?.resetForm()
  }

  useEffect(() => {
    if (formRef.current && setFormRef) {
      setFormRef(formRef)
    }
  }, [setFormRef])

  useEffect(() => {
    if (isSuccess && response?.status === "success") {
      pushSuccessToast(
        `Successfully ${isCreate ? "created" : "updated"} loan officer`
      )
      resetForm()
      onClose()
    } else if (isError || response?.status === "error") {
      pushErrorToast(`Failed to ${isCreate ? "create" : "update"} loan officer`)
    }
    setIsSubmitting(false)
  }, [
    pushSuccessToast,
    pushErrorToast,
    resetForm,
    isCreate,
    onClose,
    isSuccess,
    isError,
    response?.status
  ])

  const brand = useMemo(
    () => brands?.find(b => b.id === brandId),
    [brands, brandId]
  )

  const initialValues = useMemo(() => {
    return (
      data || {
        ...defaultValues,
        bank_nid: brandId ?? "",
        bank: brand?.name ?? ""
      }
    )
  }, [data, brandId, brand?.name])

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validate}
      onSubmit={onSubmit}
      innerRef={formRef}
      validateOnBlur
      validateOnMount={false}
      enableReinitialize
    >
      {({ values, dirty, isValid, errors }) => {
        if (Object.keys(errors).length > 0) console.error("errors", errors)
        return (
          <If
            condition={isInSidePanel}
            then={
              <SidePanel
                title={
                  isCreate
                    ? "Create Loan officer profile"
                    : "Edit Loan officer profile"
                }
                open={open}
                onClose={handleClose}
                isSubmitting={isSubmitting}
                actions={[
                  {
                    label: isCreate ? "Submit" : "Save changes",
                    type: "primary",
                    onClick: submitFormHandler,
                    disabled: loading || isLoading || !dirty || !isValid
                  }
                ]}
              >
                <ManageLoForm
                  values={values}
                  brands={brands ?? []}
                  tabsVisible={tabsVisible}
                  showLoid
                  setFormField={setFormField}
                />
              </SidePanel>
            }
            else={
              <ManageLoForm
                values={values}
                brands={brands ?? []}
                tabsVisible={tabsVisible}
                setFormField={setFormField}
              />
            }
          />
        )
      }}
    </Formik>
  )
}

export default EditLoProfile
